import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { Certificate } from '@peculiar/asn1-x509';
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
} from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';

import emptyLogo from '/icons/emptyLogo.svg';
import { Seeds as SeedsIcon } from '@/components/chariot-ui/icons';
import { DashedTag } from '@/components/chariot-ui/Tag';
import { CopyToClipboard } from '@/components/CopyToClipboard';
import { Container } from '@/components/legacy-ui';
import { Loader } from '@/components/Loader';
import { NoteModal } from '@/components/modals';
import { OverflowText } from '@/components/OverflowText';
import { Table } from '@/components/table/Table';
import Tabs from '@/components/Tabs';
import { Timeline } from '@/components/Timeline';
import { useMy } from '@/hooks';
import {
  combineAssetNodeResults,
  combineAssetRelationshipResults,
} from '@/hooks/useAssets';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useMaskingUserType } from '@/hooks/useMaskingUserType';
import { useUpdatePreseed } from '@/hooks/usePreseed';
import { NotesTab } from '@/sections/NotesTab';
import { usePreseedDrawerData } from '@/sections/preseed/hooks/usePreseedDrawerData';
import { PreseedActions } from '@/sections/preseed/PreseedActions';
import { PreseedContextMap } from '@/sections/preseed/PreseedContextMap';
import { useGlobalState } from '@/state/global.state';
import {
  Asset,
  Attribute,
  KeyboardListersId,
  Preseed,
  Seed,
  SeedStatus,
  SeedStatusLabel,
} from '@/types';
import { cn } from '@/utils/classname';
import { formatDate } from '@/utils/date.util';
import { decodeCertificate } from '@/utils/decodeCertificate.util';
import { getAccessEmail } from '@/utils/getAccessEmail';
import { maskPraetorianName } from '@/utils/userDisplay.util';

/**
 * PreseedDrawer
 *
 * KEY-BASED PATTERN: Uses preseedDrawerKey from URL params as the single
 * source of truth. The drawer fetches its own data internally via
 * usePreseedDrawerData(key), eliminating race conditions from dual state sources.
 */
export const PreseedDrawer = () => {
  const { canWriteAssets } = useEntitlements();
  const maskingUserType = useMaskingUserType();
  // KEY-BASED PATTERN: Read key from URL params via drawerState
  const { drawerState, keyboardListeners } = useGlobalState();
  const preseedKey = drawerState.preseedDrawerKey || '';

  // Fetch data internally using the key (single source of truth)
  const {
    preseed: globalAttribute,
    isLoading: preseedLoading,
    accesss,
  } = usePreseedDrawerData(preseedKey);
  const {
    data: treeNodes,
    status: treeStatus,
    error: treeError,
  } = useMy({
    resource: 'asset',
    tree: true,
    query: {
      node: {
        labels: ['Asset'],
        filters: [
          {
            field: 'source',
            operator: '=',
            value: 'seed',
          },
        ],
        relationships: [
          {
            label: ['DISCOVERED', 'HAS_ATTRIBUTE', 'HAS_PORT'],
            length: -1,
            target: {
              labels: ['Preseed'],
              filters: [
                {
                  field: 'key',
                  operator: '=',
                  value: drawerState.preseedDrawerKey,
                },
              ],
            },
          },
        ],
      },
      limit: 1000,
    },
  });

  // Extract leaf assets from each tree node path
  // A "leaf asset" is the last Asset node before a non-Asset node (e.g., Preseed)
  const assets = useMemo(() => {
    if (!treeNodes) return [];

    const leafAssets: Asset[] = [];

    for (const treeNode of treeNodes) {
      let currentNode = treeNode.node;
      let lastAsset: Asset | undefined = currentNode.result.assets?.[0];

      while (currentNode.relationships?.[0]) {
        const relationship = currentNode.relationships[0];
        const target = relationship.target;

        if (target.labels?.includes('Asset')) {
          lastAsset = target.result.assets?.[0];
          currentNode = target;
        } else {
          break;
        }
      }

      if (!lastAsset) continue;

      const isUnique = !leafAssets.some(item => item.key === lastAsset.key);
      if (isUnique) {
        leafAssets.push(lastAsset);
      }
    }

    // Sort leaf assets by group
    leafAssets.sort((a, b) => {
      const groupA = a.group || '';
      const groupB = b.group || '';
      return groupA.localeCompare(groupB);
    });

    return leafAssets;
  }, [treeNodes]);

  const [selectedAssetKey, setSelectedAssetKey] = useState<string>();
  const [sizes, setSizes] = useState([50, 50]);
  const [isTopExpanded, setIsTopExpanded] = useState(false);
  const [isBottomExpanded, setIsBottomExpanded] = useState(false);

  const [activeTab, setActiveTab] = useState('details');
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const { mutateAsync: updatePreseed } = useUpdatePreseed();

  const handleComment = useCallback(
    async (comment: string) => {
      if (!globalAttribute) return;
      await updatePreseed({ key: globalAttribute.key, comment });
    },
    [updatePreseed, globalAttribute]
  );

  // Derive selected asset: use user selection if valid, otherwise default to first asset
  const selectedAsset = useMemo(() => {
    if (selectedAssetKey) {
      const found = assets.find(a => a.key === selectedAssetKey);
      if (found) return found;
    }
    return assets[0];
  }, [assets, selectedAssetKey]);

  // useEffectEvent creates a stable function reference that always reads the latest
  // drawerState without needing it in the dependency array. This prevents excessive
  // re-renders caused by drawerState being recreated on every GlobalStateProvider render.
  const onArrowKey = useEffectEvent(() => {
    drawerState.handleOpenPreseedDrawer();
  });

  useEffect(() => {
    keyboardListeners.add(KeyboardListersId.PRESEED_DRAWER, {
      ArrowLeft: onArrowKey,
      ArrowRight: onArrowKey,
    });

    return () => {
      keyboardListeners.remove(KeyboardListersId.PRESEED_DRAWER);
    };
  }, [keyboardListeners]);

  // Use asset's group and identifier fields directly
  const selectedAssetGroup = selectedAsset?.group || '';
  const selectedAssetIdentifier = selectedAsset?.identifier || '';

  const contextMaps = useMemo(() => {
    if (!treeNodes) return [];

    const contextMaps = treeNodes.map(treeNode => {
      const result: Attribute[] = [];
      const assets: Asset[] = combineAssetNodeResults(treeNode);
      const firstAsset: Asset = assets?.[0];

      if (firstAsset) {
        // Add the initial asset
        result.push({
          ...firstAsset,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Asset type has polymorphic value field
          value: (firstAsset as any).value || '',
          capability:
            firstAsset.source === 'seed'
              ? 'seed'
              : firstAsset.capability?.[0] || '',
          metadata: treeNode.node.result.attributes?.[0]?.value || {},
        });

        let nodeLocal = treeNode.node;

        let metadata = {};
        // Traverse the relationships to build the path
        while (nodeLocal.relationships?.[0]) {
          const relationship = nodeLocal.relationships[0];
          const target = relationship.target;
          const relationshipData =
            relationship.result.discovereds?.[0] ||
            relationship.result.hasports?.[0] ||
            relationship.result.hasattributes?.[0];

          metadata = {
            capability: target.result.attributes?.[0]?.capability,
            value: target.result.attributes?.[0]?.value,
          };

          if (!relationshipData || !target.result) break;

          // Add the discovered node with its capability from the relationship
          const assets: Asset[] = combineAssetRelationshipResults(relationship);
          if (assets?.[0]) {
            const asset = assets[0];
            result.push({
              ...asset,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Asset type has polymorphic value field
              value: (asset as any).value || '',
              capability: relationshipData.capability,
              metadata: metadata,
            });
          } else if (target.result.ports?.[0]) {
            const port = target.result.ports[0];
            result.push({
              ...port,
              value: port.port,
              capability: relationshipData.capability,
              metadata: metadata,
            });
          } else if (target.result.preseeds?.[0]) {
            const preseed = target.result.preseeds[0];
            result.push({
              ...preseed,
              capability: preseed.capability || relationshipData.capability,
              metadata: metadata,
            });
          }

          nodeLocal = target;
        }

        // Add the current preseed being viewed if it exists
        if (globalAttribute) {
          const lastNode = result[result.length - 1];
          if (lastNode?.key !== globalAttribute.key) {
            result.push({
              ...globalAttribute,
              capability: globalAttribute.capability,
              metadata: metadata,
            });
          }
        }
      }
      return result;
    });

    return contextMaps;
    // Note: treeNodes is from TanStack Query which provides referential stability
    // when data hasn't changed, so we can depend on it directly
  }, [treeNodes, globalAttribute]);

  const handleSashDoubleClick = () => {
    if (!isTopExpanded && !isBottomExpanded) {
      // First double click - expand top
      setSizes([90, 10]);
      setIsTopExpanded(true);
    } else if (isTopExpanded) {
      // Second double click - expand bottom
      setSizes([5, 95]);
      setIsTopExpanded(false);
      setIsBottomExpanded(true);
    } else {
      // Third double click - back to half
      setSizes([50, 50]);
      setIsBottomExpanded(false);
    }
  };

  const matchingContextMaps = contextMaps.find(contextMap =>
    contextMap.some(attr => attr.key === selectedAsset?.key)
  );

  // Show loading state while fetching preseed data
  if (preseedLoading) {
    return (
      <div className="flex size-full items-center justify-center p-6">
        <Loader isLoading={true} />
      </div>
    );
  }

  // Show empty state if no preseed data
  if (!globalAttribute) {
    return (
      <div className="flex size-full items-center justify-center p-6">
        <div className="text-chariot-text-secondary">No preseed selected</div>
      </div>
    );
  }

  return (
    <div className="flex size-full flex-col gap-6 overflow-auto p-6">
      <div className="flex justify-between gap-4">
        <div className="flex flex-col items-start gap-2">
          <div className="flex items-center justify-between gap-2">
            <h1 className="text-chariot-text-primary text-xl font-bold">
              <CopyToClipboard alwaysShowCopyIcon>
                <OverflowText text={globalAttribute?.title ?? ''} />
              </CopyToClipboard>
            </h1>
            {globalAttribute && <PreseedActions preseeds={[globalAttribute]} />}
          </div>
        </div>
      </div>

      <Tabs
        id="preseed-drawer-tabs"
        tabs={[
          { label: 'Details', value: 'details' },
          { label: 'History', value: 'history' },
          { label: 'Notes', value: 'notes' },
        ]}
        selected={{
          label:
            activeTab === 'details'
              ? 'Details'
              : activeTab === 'history'
                ? 'History'
                : 'Notes',
          value: activeTab,
        }}
        onSelect={tab => setActiveTab(tab.value)}
      />

      {activeTab === 'details' && (
        <Container className="flex h-full min-h-0 flex-1 flex-col gap-6">
          <div className="bg-chariot-background-secondary max-h-[200px] overflow-y-auto p-4">
            <Overview globalAttribute={globalAttribute} />
          </div>

          {treeStatus === 'pending' ? (
            <Loader isLoading={true} className="flex-1" />
          ) : (
            <div className="flex-1">
              <PanelGroup direction="vertical" className="h-full">
                <Panel defaultSize={sizes[0]} minSize={10}>
                  <div className="flex h-full flex-col overflow-hidden">
                    <div className="flex items-center justify-between px-2">
                      <h3 className="text-md font-bold">Context</h3>
                      <h3 className="text-md font-bold">
                        {selectedAssetIdentifier} ({selectedAssetGroup})
                      </h3>
                    </div>
                    <div className="flex flex-1 flex-col overflow-hidden pb-2">
                      {treeError ? (
                        <div className="flex flex-1 items-center justify-center">
                          <div className="text-center">
                            <ExclamationCircleIcon className="text-chariot-text-secondary mx-auto mb-4 size-20" />
                            <h6 className="mb-2 text-xl font-bold">
                              Failed to load context map
                            </h6>
                            <p className="text-chariot-text-secondary mb-4">
                              Contact support@praetorian.com if this persists
                            </p>
                          </div>
                        </div>
                      ) : selectedAsset && contextMaps.length > 0 ? (
                        <div className="flex-1 overflow-y-auto">
                          <PreseedContextMap
                            attribute={
                              matchingContextMaps?.[0] || contextMaps[0][0]
                            }
                            sourceAttributes={
                              matchingContextMaps || contextMaps[0]
                            }
                          />
                        </div>
                      ) : (
                        <div className="text-chariot-text-secondary flex flex-1 items-center justify-center">
                          <p>No context map available</p>
                        </div>
                      )}
                    </div>
                  </div>
                </Panel>

                <PanelResizeHandle
                  className="relative h-6 cursor-row-resize"
                  onDoubleClick={handleSashDoubleClick}
                >
                  <div className="absolute inset-x-0 top-1/2 -z-50 -translate-y-1/2">
                    <div className="bg-chariot-stroke h-px w-full" />
                  </div>
                  <div className="absolute top-1/2 left-1/2 z-50 -translate-x-1/2 -translate-y-1/2">
                    <div className="border-chariot-stroke bg-chariot-background-primary hover:border-chariot-brand-primary relative flex h-6 w-20 items-center justify-center rounded-full border-2">
                      <div className="flex flex-col gap-1">
                        <div className="bg-chariot-text-secondary h-[2px] w-5" />
                        <div className="bg-chariot-text-secondary h-[2px] w-5" />
                      </div>
                    </div>
                  </div>
                </PanelResizeHandle>

                <Panel defaultSize={sizes[1]} minSize={10}>
                  <div className="mt-6 flex h-full flex-col overflow-hidden">
                    <h3 className="text-md p-2 font-bold">Assets</h3>
                    <div className="flex flex-1 flex-col overflow-hidden pt-2">
                      <Table
                        name="preseedAttributeAssets"
                        columns={[
                          {
                            label: 'Asset',
                            id: 'key',
                            className: 'pl-0',
                            cell: asset => {
                              const assetGroup = asset.group || '';
                              const assetIdentifier = asset.identifier || '';
                              const isSelected =
                                asset.key === selectedAsset?.key;
                              return (
                                <div
                                  className={cn(
                                    'py-3 pl-2',
                                    isSelected && 'bg-chariot-stroke'
                                  )}
                                >
                                  <OverflowText
                                    className={cn(
                                      'text-chariot-brand-primary shrink cursor-pointer truncate font-bold',
                                      isSelected
                                        ? 'text-chariot-text-primary'
                                        : ''
                                    )}
                                    text={assetIdentifier}
                                  />
                                  <OverflowText
                                    className="overflow-hidden text-xs"
                                    text={assetGroup}
                                  />
                                </div>
                              );
                            },
                          },
                        ]}
                        onRowClick={asset => {
                          setSelectedAssetKey(asset.key);
                        }}
                        data={assets}
                        hideTableHeader={true}
                        selection={{ hide: true }}
                        status={treeStatus}
                        error={treeError}
                      />
                    </div>
                  </div>
                </Panel>
              </PanelGroup>
            </div>
          )}
        </Container>
      )}

      {activeTab === 'history' && (
        <Container className="flex h-full min-h-0 flex-1 flex-col overflow-y-auto p-6">
          <Timeline
            items={[
              {
                title: (
                  <p>
                    <span className="font-bold">Added by</span>{' '}
                    {maskPraetorianName(
                      getAccessEmail(accesss, globalAttribute.username) || '',
                      undefined,
                      maskingUserType
                    )}
                  </p>
                ),
                updated: globalAttribute.created,
                description:
                  globalAttribute.created &&
                  formatDate(globalAttribute.created),
                className: 'text-default',
                icon: <SeedsIcon className="" />,
              },
              ...(globalAttribute.history
                ?.filter(({ from, to, updated }) => {
                  return !!from && !!to && !!updated;
                })
                ?.map(item => {
                  const by = getAccessEmail(accesss, item.by);
                  const displayBy = by
                    ? maskPraetorianName(by, undefined, maskingUserType)
                    : by;

                  return {
                    title: (
                      <div>
                        {displayBy && (
                          <p className="text-sm font-bold">{displayBy}</p>
                        )}
                        <span>
                          Changed the{' '}
                          <span className="text-base font-bold">status</span>{' '}
                          from{' '}
                          <DashedTag
                            size="xs"
                            label={SeedStatusLabel[item.from as SeedStatus]}
                          />{' '}
                          to{' '}
                          <DashedTag
                            size="xs"
                            label={SeedStatusLabel[item.to as SeedStatus]}
                          />
                        </span>
                      </div>
                    ),
                    updated: item.updated,
                    description: formatDate(item.updated),
                    className: 'text-default',
                  };
                }) || []),
            ].reverse()}
          />
        </Container>
      )}

      {activeTab === 'notes' && (
        <NotesTab
          resource={globalAttribute as unknown as Seed}
          resourceType="Preseed"
          accesss={accesss}
          onOpenCommentModal={
            canWriteAssets ? () => setCommentModalOpen(true) : undefined
          }
        />
      )}

      <NoteModal
        open={commentModalOpen}
        onClose={() => setCommentModalOpen(false)}
        onSubmit={handleComment}
        resourceType="Preseed"
        mode="simple"
        selectedCount={1}
        resource={{ key: globalAttribute.key, name: globalAttribute.title }}
        presentation="modal"
      />
    </div>
  );
};

const Overview = ({ globalAttribute }: { globalAttribute?: Preseed }) => {
  if (!globalAttribute) {
    return null;
  }

  const { display, value, type, metadata } = globalAttribute;

  // Handle cidr-handle preseeds with RIR API metadata
  if (
    type === 'cidr-handle' &&
    metadata &&
    typeof metadata === 'object' &&
    'rir_url' in metadata
  ) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Metadata type is polymorphic object
    const url = (metadata as any).rir_url as string;

    return (
      <div className="flex flex-col gap-4">
        <div className="">
          <h3 className="text-chariot-text-primary text-lg font-bold">
            RIR URL:
          </h3>
          <div>
            <CopyToClipboard textToCopy={url} alwaysShowCopyIcon>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-chariot-brand-primary hover:underline"
              >
                {url}
              </a>
            </CopyToClipboard>
          </div>
        </div>
      </div>
    );
  }

  switch (display) {
    case 'tlscert': {
      const cert = decodeCertificate(
        globalAttribute.value as unknown as Certificate
      );

      if (!cert) {
        return <div>No certificate found</div>;
      }

      return (
        <div className="flex flex-col gap-4">
          <div className="">
            <h3 className="text-chariot-text-primary text-lg font-bold">
              Subject Common Names:
            </h3>
            {cert.subjectCommonNames.length === 0 && <div>Not found</div>}
            <div>
              <CopyToClipboard alwaysShowCopyIcon>
                {cert.subjectCommonNames.join(', ')}
              </CopyToClipboard>
            </div>
          </div>
          <div className="">
            <h3 className="text-chariot-text-primary text-lg font-bold">
              DNS Names:
            </h3>
            {cert.dnsNames.length === 0 && <div>Not found</div>}
            {cert.dnsNames.map((dns: string) => (
              <div key={dns}>
                <CopyToClipboard alwaysShowCopyIcon>{dns}</CopyToClipboard>
              </div>
            ))}
          </div>
        </div>
      );
    }
    case 'image':
      return (
        <div className="bg-layer0 size-fit rounded-xs p-1.5">
          <img
            className="size-6 text-xs"
            src={value}
            alt="favicon"
            loading="lazy"
            onError={e => (e.currentTarget.src = emptyLogo)}
          />
        </div>
      );

    case 'base64':
      return (
        <CopyToClipboard textToCopy={atob(value)} alwaysShowCopyIcon>
          <div>{atob(value)}</div>
        </CopyToClipboard>
      );
    default:
      return (
        <CopyToClipboard textToCopy={value} alwaysShowCopyIcon>
          <div>{value}</div>
        </CopyToClipboard>
      );
  }
};
